/**
 * ClientOnboardingBusinessInfo Component
 * Collects business information for business entity accounts
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import { useBeforeUnload } from '../../hooks';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboarding.module.css';

const ClientOnboardingBusinessInfo = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [isTestMode] = useState(sessionStorage.getItem('onboarding_test_mode') === 'true');
    const [isSaved, setIsSaved] = useState(true);
    const [initialData, setInitialData] = useState(null);
    const [accountType, setAccountType] = useState('business');
    
    const [formData, setFormData] = useState({
        businessName: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        clientType: {
            consultant: false,
            industry: false,
            private: false,
            municipality: false,
            other: false,
            otherText: ''
        },
        applicableRegulations: {
            swda: false,
            psCode: '',
            cwa: false,
            npdes: false,
            rcra: false
        },
        additionalDocuments: ''
    });

    const [errors, setErrors] = useState({});

    // Load saved data on mount
    useEffect(() => {
        const loadSavedData = async () => {
            // Load account type from session storage
            const savedAccountType = sessionStorage.getItem(`onboarding_account_type_${token}`);
            if (savedAccountType) {
                setAccountType(savedAccountType);
            }
            
            try {
                // Try to load from backend first
                const progress = await publicTokenService.getProgress(token);
                const actualProgress = progress.progress || progress;
                
                if (actualProgress && actualProgress.stepData && actualProgress.stepData.step3) {
                    setFormData(actualProgress.stepData.step3);
                    setInitialData(JSON.stringify(actualProgress.stepData.step3));
                    return;
                }
            } catch (error) {
                // Fallback to sessionStorage
            }
            
            // Fallback to sessionStorage
            const saved = sessionStorage.getItem(`onboarding_business_info_${token}`);
            if (saved) {
                const parsedData = JSON.parse(saved);
                setFormData(parsedData);
                setInitialData(JSON.stringify(parsedData));
            } else {
                setInitialData(JSON.stringify(formData));
            }
        };
        
        loadSavedData();
    }, [token]);

    // Track unsaved changes
    useEffect(() => {
        if (initialData) {
            const hasChanges = JSON.stringify(formData) !== initialData;
            setIsSaved(!hasChanges);
        }
    }, [formData, initialData]);

    // Warn before leaving if unsaved changes
    useBeforeUnload(!isSaved, 'You have unsaved changes. Please save your progress before leaving.');

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleClientTypeChange = (type, checked) => {
        setFormData(prev => ({
            ...prev,
            clientType: {
                ...prev.clientType,
                [type]: checked
            }
        }));
    };

    const handleRegulationChange = (regulation, value) => {
        setFormData(prev => ({
            ...prev,
            applicableRegulations: {
                ...prev.applicableRegulations,
                [regulation]: value
            }
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.businessName.trim()) {
            newErrors.businessName = accountType === 'individual' ? 'Client name is required' : 'Business name is required';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (!formData.state.trim()) {
            newErrors.state = 'State is required';
        }

        if (!formData.zipCode.trim()) {
            newErrors.zipCode = 'ZIP code is required';
        } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
            newErrors.zipCode = 'Invalid ZIP code format';
        }

        const hasClientType = Object.entries(formData.clientType)
            .some(([key, value]) => key !== 'otherText' && value === true);
        
        if (!hasClientType) {
            newErrors.clientType = 'Please select at least one client type';
        }

        if (formData.clientType.other && !formData.clientType.otherText.trim()) {
            newErrors.clientTypeOther = 'Please specify the client type';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveAndExit = async () => {
        try {
            // Save to backend (step 3: Business Info)
            await publicTokenService.saveProgress(token, {
                stepNumber: 3,
                stepData: formData,
                markStepComplete: false,
                moveToNextStep: false
            });
            
            // Save current data without validation
            sessionStorage.setItem(`onboarding_business_info_${token}`, JSON.stringify(formData));
            setIsSaved(true);
            setInitialData(JSON.stringify(formData));
            alert('Your progress has been saved. You can return using the same link to continue.');
        } catch (error) {
            console.error('Failed to save progress:', error);
            alert('Failed to save progress. Please try again.');
        }
    };

    const handleContinue = async () => {
        if (validateForm()) {
            try {
                // Save to backend and mark step complete (step 3: Business Info)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 3,
                    stepData: formData,
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                // Save business info to session storage
                sessionStorage.setItem(`onboarding_business_info_${token}`, JSON.stringify(formData));
                setIsSaved(true);
                
                // Navigate to billing and reporting contacts
                navigate(`/client-onboarding/${token}/billing-reporting`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        } else {
            // Scroll to first error
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}/account-type`);
    };

    const US_STATES = [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>{accountType === 'individual' ? 'Client Information' : 'Business Information'}</h1>
                            <p className={styles.subtitle}>
                                {accountType === 'individual' ? 'Please provide your details' : 'Please provide your business details'}
                            </p>
                        </div>

                        <form className={styles.form}>
                            {/* Business/Client Name */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    {accountType === 'individual' ? 'Client Name' : 'Business Name'} <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.businessName}
                                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                                    className={`${styles.input} ${errors.businessName ? styles.inputError : ''}`}
                                    placeholder="Enter business name"
                                />
                                {errors.businessName && (
                                    <span className={styles.errorText}>{errors.businessName}</span>
                                )}
                            </div>

                            {/* Address */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Address Line 1 <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => handleInputChange('address', e.target.value)}
                                    className={`${styles.input} ${errors.address ? styles.inputError : ''}`}
                                    placeholder="Street address"
                                />
                                {errors.address && (
                                    <span className={styles.errorText}>{errors.address}</span>
                                )}
                            </div>

                            {/* Address Line 2 */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    value={formData.address2}
                                    onChange={(e) => handleInputChange('address2', e.target.value)}
                                    className={styles.input}
                                    placeholder="Apartment, suite, etc. (optional)"
                                />
                            </div>

                            {/* City, State, ZIP */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>
                                        City <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => handleInputChange('city', e.target.value)}
                                        className={`${styles.input} ${errors.city ? styles.inputError : ''}`}
                                        placeholder="City"
                                    />
                                    {errors.city && (
                                        <span className={styles.errorText}>{errors.city}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>
                                        State <span className={styles.required}>*</span>
                                    </label>
                                    <select
                                        value={formData.state}
                                        onChange={(e) => handleInputChange('state', e.target.value)}
                                        className={`${styles.select} ${errors.state ? styles.inputError : ''}`}
                                    >
                                        <option value="">Select State</option>
                                        {US_STATES.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                    {errors.state && (
                                        <span className={styles.errorText}>{errors.state}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>
                                        ZIP Code <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.zipCode}
                                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                                        className={`${styles.input} ${errors.zipCode ? styles.inputError : ''}`}
                                        placeholder="12345"
                                        maxLength="10"
                                    />
                                    {errors.zipCode && (
                                        <span className={styles.errorText}>{errors.zipCode}</span>
                                    )}
                                </div>
                            </div>

                            {/* Client Type */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Client Type <span className={styles.required}>*</span>
                                </label>
                                <div className={styles.checkboxGrid}>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.clientType.consultant}
                                            onChange={(e) => handleClientTypeChange('consultant', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Consultant</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.clientType.industry}
                                            onChange={(e) => handleClientTypeChange('industry', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Industry</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.clientType.private}
                                            onChange={(e) => handleClientTypeChange('private', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Private</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.clientType.municipality}
                                            onChange={(e) => handleClientTypeChange('municipality', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Municipality</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.clientType.other}
                                            onChange={(e) => handleClientTypeChange('other', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Other</span>
                                    </label>
                                </div>
                                {formData.clientType.other && (
                                    <input
                                        type="text"
                                        value={formData.clientType.otherText}
                                        onChange={(e) => handleClientTypeChange('otherText', e.target.value)}
                                        className={`${styles.input} ${styles.otherInput} ${errors.clientTypeOther ? styles.inputError : ''}`}
                                        placeholder="Please specify"
                                    />
                                )}
                                {errors.clientType && (
                                    <span className={styles.errorText}>{errors.clientType}</span>
                                )}
                                {errors.clientTypeOther && (
                                    <span className={styles.errorText}>{errors.clientTypeOther}</span>
                                )}
                            </div>

                            {/* Applicable Regulations */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Applicable Regulations
                                </label>
                                <div className={styles.regulationsGrid}>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.applicableRegulations.swda}
                                            onChange={(e) => handleRegulationChange('swda', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>SWDA</span>
                                    </label>

                                    <div className={styles.psCodeGroup}>
                                        <span className={styles.psCodeLabel}>PS Code:</span>
                                        <input
                                            type="text"
                                            value={formData.applicableRegulations.psCode}
                                            onChange={(e) => handleRegulationChange('psCode', e.target.value)}
                                            className={styles.psCodeInput}
                                            placeholder="Enter code"
                                        />
                                    </div>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.applicableRegulations.cwa}
                                            onChange={(e) => handleRegulationChange('cwa', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>CWA</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.applicableRegulations.npdes}
                                            onChange={(e) => handleRegulationChange('npdes', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>NPDES</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.applicableRegulations.rcra}
                                            onChange={(e) => handleRegulationChange('rcra', e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>RCRA</span>
                                    </label>
                                </div>
                                <p className={styles.helpText}>
                                    <em>If applicable, please attach copies of permits, attachment A's, or other documents concerning your monitoring requirements.</em>
                                </p>
                            </div>

                            {/* Additional Documents Note */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Additional Document Notes
                                </label>
                                <textarea
                                    value={formData.additionalDocuments}
                                    onChange={(e) => handleInputChange('additionalDocuments', e.target.value)}
                                    className={styles.textarea}
                                    placeholder="Any notes about documents you'll be providing..."
                                    rows="3"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    onClick={handleGoBack}
                                    className={styles.backButton}
                                >
                                    ← Go Back
                                </button>
                                <div className={styles.rightActions}>
                                    <button
                                        type="button"
                                        onClick={handleSaveAndExit}
                                        className={styles.saveButton}
                                    >
                                        💾 Save and Continue Later
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleContinue}
                                        className={styles.continueButton}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingBusinessInfo;
